import type React from 'react'
import { useRef, useState } from 'react'
import {
  getBottomBoundary,
  getLeftBoundary,
  getRightBoundary,
  getTopBoundary,
} from '@/lib/editor/floating-canvas-chrome-bounds'
import { clampPopoverOffset } from '../helpers/clamp-popover-offset'

/** Marks the element the drag moves; the grabber finds it with `closest`. */
export const DRAGGABLE_POPOVER_ATTR = 'data-draggable-color-popover'

const ZERO = { x: 0, y: 0 }

/**
 * Moves the color popover by its grabber, the same pointer-capture drag as the Photo Effects
 * popover. Radix keeps placing the popover beside the rail; the drag is an offset on top of that,
 * applied as a CSS `translate` so it doesn't fight Radix's own transform. `hasMoved` tells the host
 * to stop outside clicks and Escape from closing a popover the user has placed.
 */
export function usePopoverDrag() {
  const [offset, setOffset] = useState(ZERO)
  const [isDragging, setIsDragging] = useState(false)
  const [hasMoved, setHasMoved] = useState(false)
  const dragRef = useRef<{
    pointer: { x: number; y: number }
    startOffset: { x: number; y: number }
    anchor: DOMRect
  } | null>(null)

  const handleDragStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    const popover = e.currentTarget.closest<HTMLElement>(`[${DRAGGABLE_POPOVER_ATTR}]`)

    if (!popover) return
    e.preventDefault()
    e.stopPropagation()

    const rect = popover.getBoundingClientRect()

    dragRef.current = {
      pointer: { x: e.clientX, y: e.clientY },
      startOffset: offset,
      anchor: new DOMRect(rect.left - offset.x, rect.top - offset.y, rect.width, rect.height),
    }
    setIsDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleDragMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current

    if (!drag) return
    e.preventDefault()

    setOffset(
      clampPopoverOffset(
        {
          x: drag.startOffset.x + e.clientX - drag.pointer.x,
          y: drag.startOffset.y + e.clientY - drag.pointer.y,
        },
        drag.anchor,
        {
          left: getLeftBoundary(),
          top: getTopBoundary(),
          right: getRightBoundary(),
          bottom: getBottomBoundary(),
        }
      )
    )
    setHasMoved(true)
  }

  const handleDragEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current) return
    dragRef.current = null
    setIsDragging(false)

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  /** A closed popover forgets where it was dragged to; the next open anchors to the rail again. */
  const resetDrag = () => {
    dragRef.current = null
    setOffset(ZERO)
    setIsDragging(false)
    setHasMoved(false)
  }

  return {
    offset,
    isDragging,
    hasMoved,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    resetDrag,
  }
}
